import { IndexNamePair } from '@/types/floorMap/IndexNamePair'
import { FloorMapData } from '@/tmapBasicInfo/floorMapInfo/FloorMapData'

/**
 * ドラクエ9 宝の地図基本データを保持するクラス
 * @class TMapBasicData
 */
class TMapBasicData {
  /**
   * @type {number} 地図ランク
   */
  private _mapRank: number = 0

  /**
   * @type {number} 地図シード
   */
  private _mapSeed: number = 0

  /**
   * @type {IndexNamePair} 地形タイプ
   */
  private _mapType: IndexNamePair = { index: 0, name: '' }

  /**
   * @type {number} 深さ
   */
  private _mapFloorCount: number = 0

  /**
   * @type {number} B1F敵ランク
   */
  private _b1fEnemyRank: number = 0

  /**
   * @type {IndexNamePair} ボス
   */
  private _boss: IndexNamePair = { index: 0, name: '' }

  /**
   * @type {IndexNamePair} 地図名1
   */
  private _mapName1: IndexNamePair = { index: 0, name: '' }

  /**
   * @type {IndexNamePair} 地図名2
   */
  private _mapName2: IndexNamePair = { index: 0, name: '' }

  /**
   * @type {IndexNamePair} 地図名3
   */
  private _mapName3: IndexNamePair = { index: 0, name: '' }

  /**
   * @type {number} 地図レベル
   */
  private _mapLevel: number = 0

  /**
   * @type {number[]} 宝箱の数
   */
  private _treasureBoxCountList: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]

  /**
   * @type {FloorMapData[]} 全階層分のフロアマップデータ
   */
  private _floorMapDataList: FloorMapData[] = []

  /**
   * 地図ランクのgetter
   * @returns {number} 地図ランク
   */
  public get mapRank(): number {
    return this._mapRank
  }

  /**
   * 地図ランクのsetter
   * @param {number} value 地図ランク
   */
  public set mapRank(value: number) {
    this._mapRank = value
  }

  /**
   * 地図シードのgetter
   * @returns {number} 地図シード
   */
  public get mapSeed(): number {
    return this._mapSeed
  }

  /**
   * 地図シードのsetter
   * @param {number} value 地図シード
   */
  public set mapSeed(value: number) {
    this._mapSeed = value
  }

  /**
   * 地形タイプのgetter
   * @returns {IndexNamePair} 地形タイプ
   */
  public get mapType(): IndexNamePair {
    return { ...this._mapType }
  }

  /**
   * 地形タイプのsetter
   * @param {IndexNamePair} value 地形タイプ
   */
  public set mapType(value: IndexNamePair) {
    this._mapType = { ...value }
  }

  /**
   * 深さのgetter
   * @returns {number} 深さ
   */
  public get mapFloorCount(): number {
    return this._mapFloorCount
  }

  /**
   * 深さのsetter
   * @param {number} value 深さ
   */
  public set mapFloorCount(value: number) {
    this._mapFloorCount = value
  }

  /**
   * B1F敵ランクのgetter
   * @returns {number} B1F敵ランク
   */
  public get b1fEnemyRank(): number {
    return this._b1fEnemyRank
  }

  /**
   * B1F敵ランクのsetter
   * @param {number} value B1F敵ランク
   */
  public set b1fEnemyRank(value: number) {
    this._b1fEnemyRank = value
  }

  /**
   * ボスのgetter
   * @returns {IndexNamePair} ボス
   */
  public get boss(): IndexNamePair {
    return { ...this._boss }
  }

  /**
   * ボスのsetter
   * @param {IndexNamePair} value ボス
   */
  public set boss(value: IndexNamePair) {
    this._boss = { ...value }
  }

  /**
   * 地図名1のgetter
   * @returns {IndexNamePair} 地図名1
   */
  public get mapName1(): IndexNamePair {
    return { ...this._mapName1 }
  }

  /**
   * 地図名1のsetter
   * @param {IndexNamePair} value 地図名1
   */
  public set mapName1(value: IndexNamePair) {
    this._mapName1 = { ...value }
  }

  /**
   * 地図名2のgetter
   * @returns {IndexNamePair} 地図名2
   */
  public get mapName2(): IndexNamePair {
    return { ...this._mapName2 }
  }

  /**
   * 地図名2のsetter
   * @param {IndexNamePair} value 地図名2
   */
  public set mapName2(value: IndexNamePair) {
    this._mapName2 = { ...value }
  }

  /**
   * 地図名3のgetter
   * @returns {IndexNamePair} 地図名3
   */
  public get mapName3(): IndexNamePair {
    return { ...this._mapName3 }
  }

  /**
   * 地図名3のsetter
   * @param {IndexNamePair} value 地図名3
   */
  public set mapName3(value: IndexNamePair) {
    this._mapName3 = { ...value }
  }

  /**
   * 地図レベルのgetter
   * @returns {number} 地図レベル
   */
  public get mapLevel(): number {
    return this._mapLevel
  }

  /**
   * 地図レベルのsetter
   * @param {number} value 地図レベル
   */
  public set mapLevel(value: number) {
    this._mapLevel = value
  }

  /**
   * 宝箱の数のgetter
   * @returns {number[]} 宝箱の数
   */
  public get treasureBoxCountList(): number[] {
    return [...this._treasureBoxCountList]
  }

  /**
   * 宝箱の数のsetter
   * @param {number[]} value 宝箱の数
   */
  public set treasureBoxCountList(value: number[]) {
    this._treasureBoxCountList = [...value]
  }

  /**
   * 全階層分のフロアマップデータのgetter
   * @returns {FloorMapData[]} 全階層分のフロアマップデータ
   */
  public get floorMapDataList(): FloorMapData[] {
    return [...this._floorMapDataList]
  }

  /**
   * 全階層分のフロアマップデータのsetter
   * @param {FloorMapData[]} value 全階層分のフロアマップデータ
   */
  public set floorMapDataList(value: FloorMapData[]) {
    this._floorMapDataList = [...value]
  }
}

export { TMapBasicData }
